import { useState, useMemo, useCallback } from 'react';
import { ListItemType } from 'common/types/charts';
import { Folder } from 'common/types/folders';
import { TableListItemProps } from 'common/types/common';
import { DashboardDetails } from 'common/types/dashboard';
import { RawChartSchema } from 'common/types/chart';
import { getFolderType } from 'common/components/TableList/utils';

export const useFolderManager = (
  folders: Folder[],
  items: TableListItemProps[],
  type: ListItemType,
  subFolders: DashboardDetails[] | RawChartSchema[],
  currentFolderId: string | null | undefined,
) => {
  const [openFolders, setOpenFolders] = useState<Record<string, boolean>>({});
  const folderType = getFolderType(type);

  const enrichedFolders = useMemo(() => {
    return folders
      .filter(folder => folder.folder_type === folderType)
      .map(folder => ({
        ...folder,
        count: folder.items.length,
        isOpen: !!openFolders[folder.id],
      }));
  }, [folders, openFolders, folderType]);

  const folderItemIds = useMemo(() => {
    return new Set(folders.filter(folder => folder.folder_type === folderType).flatMap(folder => folder.items));
  }, [folders, folderType]);

  const unfolderedItems = useMemo(() => {
    return items.filter(item => !folderItemIds.has(item.uuid));
  }, [items, folderItemIds]);

  const toggleFolder = useCallback((folderId: string) => {
    setOpenFolders(prev => ({ ...prev, [folderId]: !prev[folderId] }));
  }, []);

  const getItemsForFolder = useCallback(
    (folderId: string) => {
      // If we have folder-specific data from our hooks, use it
      if (folderId === currentFolderId && subFolders && subFolders.length > 0) {
        // Transform subFolders to TableListItemProps format
        return subFolders.map(item => {
          // Check if it's a Dashboard or Chart
          if ('cards' in item) {
            // It's a DashboardDetails
            const dashboard = item as DashboardDetails;
            return {
              name: dashboard.name,
              uuid: dashboard.uuid,
              updated_at: dashboard.updated_at,
              owner: dashboard.owner,
              cards: dashboard.cards,
              folders: dashboard.folders || [],
            } as TableListItemProps;
          } else {
            // It's a RawChartSchema
            const chart = item as RawChartSchema;
            return {
              name: chart.name,
              uuid: chart.uuid,
              updated_at: chart.updated_at,
              owner: chart.owner,
              chart_type: chart.chart_type,
              folders: chart.folders || [],
            } as TableListItemProps;
          }
        });
      }

      // Fallback to the original implementation
      const folderItemSet = new Set(folders.find(folder => String(folder.id) === String(folderId))?.items || []);

      return items.filter(item => folderItemSet.has(item.uuid));
    },
    [items, subFolders, currentFolderId, folders],
  );

  return {
    enrichedFolders,
    unfolderedItems,
    setOpenFolders,
    toggleFolder,
    getItemsForFolder,
  };
};
